import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  combineDateTimeInputValue,
  splitDateTimeInputValue,
} from '../utils/localization';
import { LocalizedDateInput } from './LocalizedDateInput';
import { TimeInput24 } from './TimeInput24';
import styles from './LocalizedDateTimeInput.module.css';

export interface LocalizedDateTimeInputProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value: string;
  onValueChange?: (value: string) => void;
  defaultTime?: string;
  disabled?: boolean;
  dateInputId?: string;
  timeInputId?: string;
  className?: string;
  dateClassName?: string;
  timeClassName?: string;
}

export const LocalizedDateTimeInput: React.FC<LocalizedDateTimeInputProps> = ({
  value,
  onValueChange,
  defaultTime = '09:00',
  disabled = false,
  dateInputId,
  timeInputId,
  className,
  dateClassName,
  timeClassName,
  ...rest
}) => {
  const { t } = useTranslation(['calendar', 'common']);
  const { date, time } = splitDateTimeInputValue(value);

  const handleDateChange = (nextDate: string) => {
    onValueChange?.(combineDateTimeInputValue(nextDate, time || defaultTime));
  };

  const handleTimeChange = (nextTime: string) => {
    if (!date) {
      return;
    }

    onValueChange?.(combineDateTimeInputValue(date, nextTime || defaultTime));
  };

  return (
    <div className={[styles.row, className].filter(Boolean).join(' ')} {...rest}>
      <LocalizedDateInput
        id={dateInputId}
        className={['text-input', styles.date, dateClassName].filter(Boolean).join(' ')}
        value={date}
        onValueChange={handleDateChange}
        disabled={disabled}
        fillWidth
      />
      <TimeInput24
        id={timeInputId}
        className={['text-input', styles.time, timeClassName].filter(Boolean).join(' ')}
        value={time}
        onChange={handleTimeChange}
        disabled={disabled || !date}
        aria-label={t('time')}
      />
    </div>
  );
};
